/**
 * JavaScript code to detect available availability of a
 * particular font in a browser using JavaScript and CSS.
 *
 * Author : Lalit Patel
 * Website: http://www.lalit.org/lab/javascript-css-font-detect/
 * License: Apache Software License 2.0
 *          http://www.apache.org/licenses/LICENSE-2.0
 * Version: 0.15 (21 Sep 2009)
 *          Changed comparision font to default from sans-default-default,
 *          as in FF3.0 font of child element didn't fallback
 *          to parent element if the font is missing.
 * Version: 0.2 (04 Mar 2012)
 *          Comparing font against all the 3 generic font families ie,
 *          'monospace', 'sans-serif' and 'sans'. If it doesn't match all 3
 *          then that font is 100% not available in the system
 * Version: 0.3 (24 Mar 2012)
 *          Replaced sans with serif in the list of baseFonts
 */

/**
 * Copyright IBM Corp. 2021
 *
 * This source code is licensed under the IBM Public License Version 1.0 found in the
 * LICENSE file in the root directory of this source tree.
 */

const DEFAULT_FONTS = {
  "Andale Mono": `"andale mono"`,
  Arial: "arial",
  "Arial Black": `"arial black"`,
  "Book Antiqua": `"book antiqua"`,
  "Comic Sans MS": `"comic sans ms"`,
  "Courier New": `"courier new", courier`,
  Georgia: "georgia",
  "IBM Plex Sans": `"IBM Plex Sans"`,
  Impact: "Impact",
  Roboto: "Roboto",
  Symbol: "symbol",
  Tahoma: "tahoma",
  Terminal: "terminal",
  "Times New Roman": `"times new roman",times`,
  "Trebuchet MS": `"trebuchet ms"`,
  Verdana: "verdana",
  Webdings: "webdings",
  Wingdings: `wingdings`,
};

const BASE_FONTS_TO_LOAD = [
  new FontFace(
    "Impact",
    `local("Impact"),
     url(https://fonts.gstatic.com/l/font?kit=HhyVU5gq9PCuP11_LNCHOoxCTQ&skey=5128cd07406988e&v=v9)
    format("woff2")`
  ),
  new FontFace(
    "Trebuchet MS",
    `local("Trebuchet MS"), local("TrebuchetMS"),
     url(https://fonts.gstatic.com/l/font?kit=5h1diZ4yJ3MPiLu-OzLLCj-qCm5MjXPjbA&skey=7e071cef4f2cf8ce&v=v9)
    format("woff2")`
  ),
  new FontFace(
    "Roboto",
    `local("Roboto"), local("Roboto-Regular"),
     url(https://fonts.gstatic.com/s/roboto/v20/KFOmCnqEu92Fr1Mu4mxKKTU1Kg.woff2)
    format("woff2")`
  ),
  new FontFace(
    "IBM Plex Sans",
    `local("IBM Plex Sans"), local("IBMPlexSans"),
     url(https://fonts.gstatic.com/s/ibmplexsans/v8/zYXgKVElMYYaJe8bpLHnCwDKhdHeFaxOedc.woff2)
     format("woff2")`
  ),
];

const Detector = function () {
  // a font will be compared against all the three default fonts.
  // and if it doesn't match all 3 then that font is not available.
  const baseFonts = ["IBM Plex Sans", "trebuchet ms", "Impact"];

  const defaultWidth = {};
  for (const index in baseFonts) {
    defaultWidth[baseFonts[index]] = getSize(baseFonts[index]); // width for the default font
  }

  const robotoWidth = getSize("Roboto");

  function detect(font) {
    let detected = false;
    for (const index in baseFonts) {
      const offsetWidth = getSize(font + "," + baseFonts[index]); // name of the font along with the base font for fallback.
      const matched = offsetWidth !== defaultWidth[baseFonts[index]];
      detected = detected || matched;
    }
    return detected && (font === "Roboto" || getSize(font) !== robotoWidth);
  }

  this.detect = detect;
};

function getSize(fontFamily) {
  // we use m or w because these two characters take up the maximum width.
  // And we use a LLi so that the same matching fonts can get separated
  const testString = "mmmmmmmmmmlli";

  // we test using 72px font size, we may use any size. I guess larger the better.
  const testSize = "72px";
  const span = document.createElement("span");
  span.style.fontSize = testSize;
  span.innerHTML = testString;
  span.style.fontFamily = fontFamily;
  const body = getBody();
  body.appendChild(span);
  const offsetWidth = span.offsetWidth; // width for the default font
  body.removeChild(span);
  return offsetWidth;
}

let body = null;

function getBody() {
  if (body === null) {
    body = document.getElementsByTagName("body")[0];
  }
  return body;
}

let availableFonts = null;

export async function getTriNoteAvailableFonts() {
  if (availableFonts === null) {
    await Promise.all(
      BASE_FONTS_TO_LOAD.map((fontFace) =>
        fontFace.load().then((font) => document.fonts.add(font))
      )
    );
    const detector = new Detector();
    availableFonts = "";
    for (const [key, value] of Object.entries(DEFAULT_FONTS)) {
      if (detector.detect(value)) {
        availableFonts += `${key}=${value};`;
      }
    }
  }
  return availableFonts;
}
